import React, { useState, useEffect } from "react";
import "./DiscordArchivePage.css";

/// チャンネルタイプの定義
type ChannelType = "notice" | "promise" | "request" | "tweet";

/// チャンネル情報の型
interface ChannelInfo {
  id: ChannelType;
  title: string;
  description: string;
  icon: string;
}

/// 利用可能なチャンネル一覧
const CHANNELS: ChannelInfo[] = [
  {
    id: "notice",
    title: "お知らせ",
    description: "サーバーからのお知らせ",
    icon: "📢",
  },
  {
    id: "promise",
    title: "マールとのお約束",
    description: "サーバーのルール・規約",
    icon: "📜",
  },
  {
    id: "tweet",
    title: "マールのつぶやき",
    description: "マールさんのつぶやき",
    icon: "💭",
  },
  {
    id: "request",
    title: "マールへの歌リク",
    description: "歌リクエストのログ",
    icon: "🎵",
  },
];

/// Discordアーカイブページ
const DiscordArchivePage: React.FC = () => {
  const [passcode, setPasscode] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  /// マウント時に認証状態を確認
  useEffect(() => {
    document.body.classList.add("discord-archive-page-body");
    checkAuthStatus();

    return () => {
      document.body.classList.remove("discord-archive-page-body");
    };
  }, []);

  /// 認証状態の確認
  const checkAuthStatus = async () => {
    try {
      const response = await fetch("/api/discord/status", {
        method: "GET",
        credentials: "include",
      });
      const data = (await response.json()) as { authenticated?: boolean };
      if (data?.authenticated) {
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error("認証状態確認エラー:", error);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  /// ログイン処理
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await fetch("/api/discord/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ passcode }),
      });

      if (response.ok) {
        setIsAuthenticated(true);
        setPasscode("");
      } else if (response.status === 401) {
        setErrorMessage("パスコードが正しくありません");
      } else {
        setErrorMessage("ログインに失敗しました");
      }
    } catch (error) {
      console.error("ログインエラー:", error);
      setErrorMessage("通信エラーが発生しました");
    } finally {
      setIsLoading(false);
    }
  };

  /// チャンネル選択（同一タブで遷移）
  const handleChannelSelect = (channelId: ChannelType) => {
    window.location.href = `/discord/${channelId}`;
  };

  /// 認証確認中の表示
  if (isCheckingAuth) {
    return (
      <div className="discord-archive-page">
        <div className="discord-archive-container">
          <div className="discord-archive-loading">
            <div className="loading-spinner" />
            <p>認証状態を確認中...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="discord-archive-page">
      <div className="discord-archive-container">
        {/* ヘッダー */}
        <header className="discord-archive-header">
          <div className="header-icon">🐑</div>
          <h1 className="header-title">マールの羊小屋</h1>
          <p className="header-subtitle">アーカイブ</p>
        </header>

        {/* メインコンテンツ */}
        <main className="discord-archive-main">
          {!isAuthenticated ? (
            /* 認証フォーム */
            <div className="auth-section">
              <div className="auth-card">
                <div className="auth-icon">🔐</div>
                <h2 className="auth-title">アクセス認証</h2>
                <p className="auth-description">
                  このアーカイブを閲覧するには
                  <br />
                  パスコードが必要です
                </p>

                <form onSubmit={handleLogin} className="auth-form">
                  <div className="input-wrapper">
                    <input
                      type="password"
                      value={passcode}
                      onChange={(e) => setPasscode(e.target.value)}
                      placeholder="パスコードを入力"
                      className="auth-input"
                      disabled={isLoading}
                      autoComplete="off"
                    />
                  </div>

                  {errorMessage && (
                    <div className="error-message">
                      <span className="error-icon">⚠️</span>
                      {errorMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="auth-button"
                    disabled={isLoading || !passcode.trim()}
                  >
                    {isLoading ? (
                      <span className="button-loading">
                        <span className="loading-spinner-small" />
                        認証中...
                      </span>
                    ) : (
                      "ログイン"
                    )}
                  </button>
                </form>
              </div>
            </div>
          ) : (
            /* 認証済みコンテンツ */
            <div className="content-section">
              {/* チャンネル選択グリッド */}
              <div className="channel-grid">
                {CHANNELS.map((channel) => (
                  <button
                    key={channel.id}
                    className="channel-card"
                    onClick={() => handleChannelSelect(channel.id)}
                  >
                    <span className="channel-icon">{channel.icon}</span>
                    <span className="channel-title">{channel.title}</span>
                    <span className="channel-description">{channel.description}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </main>

        {/* フッター */}
        <footer className="discord-archive-footer">
          <p className="footer-note">
            このアーカイブは「マールの羊小屋」Discordサーバーの記録です
          </p>
        </footer>
      </div>
    </div>
  );
};

export default DiscordArchivePage;

